import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  RefreshCw,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';
import axios from 'axios';
import {
  remindersApi,
  type ReminderCategory,
  type SubscriberReminderSummaryRow,
} from '../api/reminders.api';
import { subscribersApi } from '../api/subscribers.api';

function getQueryErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const apiMessage = error.response?.data?.error?.message;
    if (typeof apiMessage === 'string' && apiMessage.length > 0) {
      return apiMessage;
    }
    if (error.response?.status === 404) {
      return 'Reminder summary API is unavailable. Restart the backend server and try again.';
    }
    if (error.response?.status) {
      return `Request failed (${error.response.status}). Try again or restart the backend.`;
    }
  }
  return 'Failed to load reminder summary';
}

function formatLastReminderSent(
  timestamp: string | null | undefined,
  hasContact: boolean,
  missingLabel: string
): string {
  if (!hasContact) return missingLabel;
  if (!timestamp) return '—';
  return new Date(timestamp).toLocaleString('en-IN');
}

function ReminderCategoryBadge({ category }: { category: ReminderCategory | null }) {
  if (!category) return null;

  const labels: Record<ReminderCategory, string> = {
    upcoming_payment: 'Upcoming',
    pending_dues: 'Pending dues',
  };
  const styles: Record<ReminderCategory, string> = {
    upcoming_payment: 'bg-blue-50 text-blue-700 border-blue-200',
    pending_dues: 'bg-amber-50 text-amber-800 border-amber-200',
  };

  return (
    <span
      className={`inline-flex mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${styles[category]}`}
    >
      {labels[category]}
    </span>
  );
}

function SubscriberReminderHistoryPanel({
  row,
  onBack,
}: {
  row: SubscriberReminderSummaryRow;
  onBack: () => void;
}) {
  const [historyPage, setHistoryPage] = useState(1);
  const historyLimit = 20;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['subscriber-reminder-history', row.subscriber.id, historyPage, historyLimit],
    queryFn: () =>
      subscribersApi.getReminderHistory(row.subscriber.id, {
        page: historyPage,
        limit: historyLimit,
      }),
  });

  const reminders = data?.reminders ?? [];
  const pagination = data?.pagination;

  return (
    <div className="min-w-0 max-w-full">
      <div className="mb-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 px-2 py-1.5 -ml-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Reminders
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {row.subscriber.fullname || 'Unknown subscriber'}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
              {row.subscriber.subscriberCode && (
                <span className="font-mono">{row.subscriber.subscriberCode}</span>
              )}
              {row.subscriber.phone && <span>{row.subscriber.phone}</span>}
              {row.subscriber.email && <span>{row.subscriber.email}</span>}
            </div>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-900">Reminder History</h2>
        </div>

        {isLoading ? (
          <div className="p-12 text-center text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
            Loading reminder history…
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertCircle className="w-8 h-8 mx-auto text-red-400 mb-4" />
            <p className="text-red-500">Failed to load reminder history</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-4 text-blue-600 hover:text-blue-800 text-sm"
            >
              Try again
            </button>
          </div>
        ) : reminders.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Bell className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <p className="font-medium text-gray-700">No reminders sent yet</p>
          </div>
        ) : (
          <>
            <table className="w-full min-w-[720px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Channel
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Reason
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Invoice
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reminders.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {new Date(item.sentAt || item.createdAt).toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 capitalize">
                      {item.channel || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 capitalize">
                      {item.source || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 capitalize">{item.status}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {item.skipReason || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {item.invoiceNumber || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium whitespace-nowrap">
                      {item.amount != null ? `₹${Number(item.amount).toFixed(2)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                    disabled={historyPage <= 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setHistoryPage((p) => p + 1)}
                    disabled={historyPage >= pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function SubscriberRemindersPage() {
  const [selectedSubscriber, setSelectedSubscriber] =
    useState<SubscriberReminderSummaryRow | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [hasReminderFilter, setHasReminderFilter] = useState<'all' | 'yes' | 'no'>('all');
  const perPage = 20;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['subscriber-reminder-summary', page, perPage, search, hasReminderFilter],
    queryFn: () =>
      remindersApi.listSubscriberReminderSummary({
        page,
        perPage,
        search: search || undefined,
        hasReminder:
          hasReminderFilter === 'all' ? undefined : hasReminderFilter === 'yes',
      }),
    retry: 1,
    enabled: !selectedSubscriber,
  });

  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const renderReminderCell = (
    row: SubscriberReminderSummaryRow,
    channel: 'whatsapp' | 'email'
  ) => {
    const summary = channel === 'whatsapp' ? row.whatsapp : row.email;
    const hasContact =
      channel === 'whatsapp'
        ? Boolean(row.subscriber.phone)
        : Boolean(row.subscriber.email);

    return (
      <div>
        <div>
          {formatLastReminderSent(
            summary.lastSentAt,
            hasContact,
            channel === 'whatsapp' ? 'No phone' : 'No email'
          )}
        </div>
        <ReminderCategoryBadge category={summary.category} />
      </div>
    );
  };

  if (selectedSubscriber) {
    return (
      <SubscriberReminderHistoryPanel
        row={selectedSubscriber}
        onBack={() => setSelectedSubscriber(null)}
      />
    );
  }

  return (
    <div className="min-w-0 max-w-full">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-7 h-7 text-blue-600" />
            Reminders
          </h1>
          <p className="text-gray-600 mt-1">
            Last reminder sent per subscriber. Click a row to view full reminder history.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <form onSubmit={handleSearchSubmit} className="flex gap-2 flex-1 min-w-[200px] max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search subscriber…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Search
          </button>
        </form>

        <select
          value={hasReminderFilter}
          onChange={(e) => {
            setHasReminderFilter(e.target.value as 'all' | 'yes' | 'no');
            setPage(1);
          }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All subscribers</option>
          <option value="yes">Reminded at least once</option>
          <option value="no">Never reminded</option>
        </select>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
            Loading reminder summary…
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertCircle className="w-8 h-8 mx-auto text-red-400 mb-4" />
            <p className="text-red-500">{getQueryErrorMessage(error)}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-4 text-blue-600 hover:text-blue-800 text-sm"
            >
              Try again
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Bell className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <p className="font-medium text-gray-700">No subscribers found</p>
            <p className="text-sm mt-1">Adjust your search or filters.</p>
          </div>
        ) : (
          <>
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Subscriber
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Amount Pending
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Last WhatsApp Reminder
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Last Email Reminder
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((row) => (
                  <tr
                    key={row.subscriber.id}
                    onClick={() => setSelectedSubscriber(row)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {row.subscriber.fullname || 'Unknown'}
                      </div>
                      {row.subscriber.subscriberCode && (
                        <div className="text-xs text-gray-500 font-mono">
                          {row.subscriber.subscriberCode}
                        </div>
                      )}
                      {row.subscriber.phone && (
                        <div className="text-xs text-gray-500 mt-0.5">{row.subscriber.phone}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                      ₹{row.pendingAmount.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {renderReminderCell(row, 'whatsapp')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {renderReminderCell(row, 'email')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
